import React from "react";

type CustomerIconProps = {
  /** "scan" = customer's face inside mood-scan brackets, "chat" = customer with a speech bubble. */
  variant: "scan" | "chat";
  className?: string;
};

/**
 * Simple, friendly icon of an anonymous customer — a normal everyday person
 * with neutral features (no bartender styling, no exotic glowing eyes), since
 * these buttons are about the customer themselves. Decorated to signal what
 * each AI-Pick button does:
 *   • "scan" — the customer's face inside a mood-scan reticle with a sweeping line.
 *   • "chat" — the customer with a speech bubble, like they're describing their vibe.
 * Flat fills, square 100×100 viewBox so it scales cleanly inside a badge. The
 * green accents live only in the scan/chat cues, not on the person.
 */
export default function CustomerIcon({ variant, className = "" }: CustomerIconProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-hidden
    >
      {/* Shoulders — plain casual shirt */}
      <path d="M24 96 C 24 80 35 73 50 73 C 65 73 76 80 76 96 Z" fill="#5b6b78" />
      {/* Crew neckline */}
      <path d="M42 74 Q50 80 58 74" stroke="#46545f" strokeWidth="2" strokeLinecap="round" fill="none" />
      {/* Neck */}
      <rect x="45.5" y="58" width="9" height="17" rx="3" fill="#e7b48f" />

      {/* Ears */}
      <ellipse cx="30" cy="45" rx="3.1" ry="5.2" fill="#f1c8a2" />
      <ellipse cx="70" cy="45" rx="3.1" ry="5.2" fill="#f1c8a2" />
      {/* Head */}
      <ellipse cx="50" cy="43" rx="20" ry="23" fill="#f1c8a2" />
      {/* Hair — simple rounded cap */}
      <path d="M29 45 C 29 22 40 13 50 13 C 60 13 71 22 71 45 C 71 35 64 29 50 29 C 36 29 29 35 29 45 Z" fill="#6b5444" />
      {/* Brows */}
      <path d="M38 41 Q42 39 46 41" stroke="#5a4738" strokeWidth="1.4" strokeLinecap="round" fill="none" />
      <path d="M54 41 Q58 39 62 41" stroke="#5a4738" strokeWidth="1.4" strokeLinecap="round" fill="none" />
      {/* Eyes — normal dark eyes with an occasional blink */}
      <g>
        <ellipse cx="42" cy="47" rx="2.6" ry="3.2" fill="#2f2a26" />
        <ellipse cx="58" cy="47" rx="2.6" ry="3.2" fill="#2f2a26" />
        <circle cx="42.8" cy="45.8" r="0.8" fill="#ffffff" />
        <circle cx="58.8" cy="45.8" r="0.8" fill="#ffffff" />
        <rect x="38.6" y="43.4" width="6.8" height="0" rx="1.4" fill="#f1c8a2">
          <animate attributeName="height" values="0;0;6.4;0;0" keyTimes="0;0.92;0.95;0.98;1" dur="5s" repeatCount="indefinite" />
        </rect>
        <rect x="54.6" y="43.4" width="6.8" height="0" rx="1.4" fill="#f1c8a2">
          <animate attributeName="height" values="0;0;6.4;0;0" keyTimes="0;0.92;0.95;0.98;1" dur="5s" repeatCount="indefinite" />
        </rect>
      </g>
      {/* Nose */}
      <path d="M50 49 Q48.5 54 50 57" stroke="#dcae87" strokeWidth="1.1" strokeLinecap="round" fill="none" />
      {/* Smile */}
      <path d="M43 60 Q50 66 57 60" stroke="#8a5a3a" strokeWidth="1.8" strokeLinecap="round" fill="none" />
      {/* Cheeks */}
      <ellipse cx="37" cy="55" rx="3.4" ry="2.1" fill="#ef9a8a" opacity="0.22" />
      <ellipse cx="63" cy="55" rx="3.4" ry="2.1" fill="#ef9a8a" opacity="0.22" />

      {variant === "scan" ? (
        /* Mood-scan reticle: corner brackets + sweeping scan line */
        <g stroke="#6fd94a" strokeLinecap="round" fill="none">
          <g strokeWidth="2.6">
            <path d="M9 22 L9 9 L22 9" />
            <path d="M78 9 L91 9 L91 22" />
            <path d="M91 78 L91 91 L78 91" />
            <path d="M22 91 L9 91 L9 78" />
          </g>
          <line x1="13" y1="46" x2="87" y2="46" strokeWidth="1.8">
            <animate attributeName="y1" values="24;72;24" dur="2.8s" repeatCount="indefinite" />
            <animate attributeName="y2" values="24;72;24" dur="2.8s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.25;0.7;0.25" dur="2.8s" repeatCount="indefinite" />
          </line>
        </g>
      ) : (
        /* Speech bubble — the customer is chatting */
        <g>
          <path
            d="M58 5 H93 Q97 5 97 9 V25 Q97 29 93 29 H71 L64 37 L65 29 H58 Q54 29 54 25 V9 Q54 5 58 5 Z"
            fill="#8af36a"
            stroke="#41b827"
            strokeWidth="1"
          />
          <g fill="#08210a">
            <circle cx="65" cy="17" r="2.1">
              <animate attributeName="opacity" values="0.3;1;0.3" dur="1.4s" repeatCount="indefinite" begin="0s" />
            </circle>
            <circle cx="75.5" cy="17" r="2.1">
              <animate attributeName="opacity" values="0.3;1;0.3" dur="1.4s" repeatCount="indefinite" begin="0.25s" />
            </circle>
            <circle cx="86" cy="17" r="2.1">
              <animate attributeName="opacity" values="0.3;1;0.3" dur="1.4s" repeatCount="indefinite" begin="0.5s" />
            </circle>
          </g>
        </g>
      )}
    </svg>
  );
}
